import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Anchor, Plus, RefreshCw, Trash2, Users } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const AdminPromoCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", max_uses: 250, validity_days: 30, label: "VIP Capital Club Pioneer" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promo-codes");
      setCodes(r.data.codes || []);
    } catch {
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!newCode.code.trim()) {
      toast.error("Code is required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/promo-codes", newCode);
      toast.success("Promo code created");
      setNewCode({ code: "", max_uses: 250, validity_days: 30, label: "VIP Capital Club Pioneer" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const extend = async (id) => {
    try {
      await api.post(`/admin/promo-codes/${id}/extend`, null, { params: { days: 30 } });
      toast.success("Extended 30 days");
      load();
    } catch {
      toast.error("Failed to extend");
    }
  };

  // STARSEED-CAPMARKETS-059: mark ONE code as the founding-member auto-apply
  // offer — the registration page pre-fills it for every new member while
  // seats remain. Turning it on clears the flag from any other code.
  const toggleAutoApply = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { auto_apply: !c.auto_apply });
      toast.success(c.auto_apply
        ? "Auto-apply disabled — members must enter a code again."
        : `${c.code} is now auto-applied to every new registration.`);
      load();
    } catch {
      toast.error("Failed to toggle auto-apply");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { is_active: !c.is_active });
      toast.success(c.is_active ? "Disabled" : "Re-enabled");
      load();
    } catch {
      toast.error("Failed to toggle");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Permanently delete "${c.code}"? Redemptions history will remain.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const isExpired = (iso) => iso && new Date(iso) < new Date();

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-promo-codes-page">
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-1">
            <Anchor className="w-5 h-5 text-amber-600" />
            <p className="text-amber-700 text-xs tracking-[0.3em] uppercase">Pioneer Codes</p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif text-slate-900">VIP Capital Club Pioneer Codes</h1>
          <p className="text-slate-600 mt-2 text-sm max-w-3xl">
            Codes waive the <strong>$999 one-time join fee for the first month</strong>, so members
            can see real returns before paying it. Pioneer-coded members still pay <strong>$99/month</strong>
            and receive a lifetime Pioneer number + member badge.
            Rotate codes monthly for security.
          </p>
        </div>

        {/* Create — amber callout. Per-element explicit colors so this
            card is contrast-correct even if the global `.dashboard-shell`
            remapper in index.css doesn't apply (e.g. when this page is
            rendered outside the dashboard shell). */}
        <Card className="border-amber-200 bg-amber-50/60 mb-6">
          <CardContent className="p-5">
            <p className="text-amber-900 text-xs uppercase tracking-widest font-semibold mb-3 flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Issue a New Code
            </p>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-900 text-xs font-semibold">Code</Label>
                <Input
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                  placeholder="YACHT101"
                  className="uppercase bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  data-testid="new-code-input"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs font-semibold">Label</Label>
                <Input
                  value={newCode.label}
                  onChange={(e) => setNewCode({ ...newCode, label: e.target.value })}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  data-testid="new-code-label"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs font-semibold">Max Uses</Label>
                <Input
                  type="number" min={1} max={10000}
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value || "1") })}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  data-testid="new-code-uses"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs font-semibold">Valid (days)</Label>
                <Input
                  type="number" min={1} max={365}
                  value={newCode.validity_days}
                  onChange={(e) => setNewCode({ ...newCode, validity_days: parseInt(e.target.value || "30") })}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                  data-testid="new-code-validity"
                />
              </div>
            </div>
            <div className="mt-4">
              <Button
                onClick={save}
                disabled={creating}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="new-code-create-btn"
              >
                {creating ? "Creating…" : "Issue Code"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <p className="text-slate-500 text-sm py-10 text-center">Loading…</p>
        ) : codes.length === 0 ? (
          <Card className="bg-white border-slate-200"><CardContent className="p-10 text-center text-slate-500">No promo codes yet.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {codes.map((c) => {
              const remaining = Math.max(0, (c.max_uses || 0) - (c.uses || 0));
              const expired = isExpired(c.expires_at);
              return (
                <Card key={c.id} className={`bg-white border-slate-200 ${expired ? "opacity-60" : ""}`} data-testid={`promo-code-${c.code}`}>
                  <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-lg font-semibold text-slate-900 tracking-wider">{c.code}</span>
                        {expired
                          ? <Badge variant="outline" className="border-red-300 text-red-700">Expired</Badge>
                          : c.is_active
                            ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                            : <Badge variant="outline">Disabled</Badge>}
                        {c.is_shared && <Badge variant="outline" className="border-amber-300 text-amber-800">Shared</Badge>}
                        {c.auto_apply && <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-testid={`promo-auto-badge-${c.code}`}>Auto-applied at signup</Badge>}
                      </div>
                      <p className="text-slate-600 text-xs">{c.label}</p>
                      <div className="flex gap-4 text-xs text-slate-500 mt-2 flex-wrap">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.uses}/{c.max_uses} used · {remaining} left</span>
                        <span>Expires {formatDate(c.expires_at)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        size="sm" variant={c.auto_apply ? "default" : "outline"}
                        onClick={() => toggleAutoApply(c)}
                        className={c.auto_apply
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "border-blue-300 text-blue-800 hover:bg-blue-50"}
                        data-testid={`promo-auto-apply-${c.code}`}
                      >
                        {c.auto_apply ? "Auto-apply ON" : "Auto-apply"}
                      </Button>
                      <Switch
                        checked={c.is_active}
                        onCheckedChange={() => toggleActive(c)}
                        data-testid={`promo-active-${c.code}`}
                      />
                      <Button
                        size="sm" variant="outline"
                        onClick={() => extend(c.id)}
                        className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        data-testid={`promo-extend-${c.code}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> +30d
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => del(c)}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`promo-delete-${c.code}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminPromoCodesPage;
